<template>
  <Dialog
    v-model:visible="showDialog"
    :header="t('wkt.client.expensesheets.confirm-ticket-dialog.title')"
    :modal="true"
    :closable="true"
    :draggable="false"
    class="custom-dialog"
    :style="{ width: isMobile ? '100vw' : '90vw', maxWidth: '1150px' }"
    :breakpoints="{ '768px': '100vw' }"
  >
    <div class="confirm-content">
      <div class="info-section">
        <i class="pi pi-info-circle" style="font-size: 2rem; color: var(--primary-color)"></i>
        <h3 class="info-text">
          {{ t("wkt.client.expensesheets.confirm-ticket-dialog.info") }}
        </h3>
      </div>

      <div class="form-grid form-content">
        <div class="form-field">
          <label class="field-label">
            {{ t("wkt.client.expensesheets.edit-item-dialog.ticket_date") }}
          </label>
          <InputText v-model="data.date" readonly />
        </div>

        <div class="form-field">
          <label class="field-label">
            {{ t("wkt.client.expensesheets.edit-item-dialog.ticket_time") }}
          </label>
          <InputText v-model="data.time" readonly />
        </div>

        <div class="form-field">
          <label class="field-label">
            {{
              t("wkt.client.expensesheets.edit-item-dialog.vendor_vat_number")
            }}
          </label>
          <InputText v-model="data.vendorVatNumber" readonly />
        </div>

        <div class="form-field">
          <label class="field-label">
            {{ t("wkt.client.expensesheets.edit-item-dialog.vendor_name") }}
          </label>
          <InputText v-model="data.vendorName" readonly />
        </div>

        <div class="form-field">
          <label class="field-label">
            {{ t("wkt.client.expensesheets.edit-item-dialog.taxes_percent") }}
          </label>
          <InputText v-model="data.taxesPercent" readonly />
        </div>

        <div class="form-field">
          <label class="field-label">
            {{ t("wkt.client.expensesheets.edit-item-dialog.taxes_amount") }}
          </label>
          <InputText v-model="data.taxesAmount" readonly />
        </div>

        <div class="form-field">
          <label class="field-label">
            {{ t("wkt.client.expensesheets.edit-item-dialog.gross_amount") }}
          </label>
          <InputText v-model="data.grossAmount" readonly />
        </div>

        <div class="form-field">
          <label class="field-label">
            {{ t("wkt.client.expensesheets.edit-item-dialog.total_amount") }}
          </label>
          <InputText v-model="data.totalAmount" readonly />
        </div>

        <div class="form-field full-width">
          <label class="field-label">
            {{ t("wkt.client.expensesheets.edit-item-dialog.ticket_number") }}
          </label>
          <InputText v-model="data.ticketNumber" readonly />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <Button
          :label="t('wkt.client.expensesheets.edit-item-dialog.button-add')"
          class="footer-primary-button"
          color="#185EA9"
          variant="text"
          @click="ok()"
        />
        <Button
          :label="t('wkt.client.expensesheets.edit-item-dialog.button-reject')"
          class="footer-button-cancel"
          variant="text"
          @click="close()"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useUtils } from "@wkt/composables/useUtils";
import { onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { formatNumberToLocale } = useUtils();

const showDialog = ref(false);
const data = ref<any>({});
const msgCb = ref<(() => void) | null>(null);
const isMobile = ref(false);

function checkMobile() {
  isMobile.value = window.innerWidth < 768;
}

function open(ticketData: any, callback?: () => void) {
  // Formatear valores numéricos del backend al formato del locale
  data.value = {
    ...ticketData,
    taxesPercent: ticketData.taxesPercent
      ? formatNumberToLocale(ticketData.taxesPercent)
      : ticketData.taxesPercent,
    taxesAmount: ticketData.taxesAmount
      ? formatNumberToLocale(ticketData.taxesAmount)
      : ticketData.taxesAmount,
    grossAmount: ticketData.grossAmount
      ? formatNumberToLocale(ticketData.grossAmount)
      : ticketData.grossAmount,
    totalAmount: ticketData.totalAmount
      ? formatNumberToLocale(ticketData.totalAmount)
      : ticketData.totalAmount,
  };
  msgCb.value = callback || null;
  showDialog.value = true;
}

function close() {
  showDialog.value = false;
  msgCb.value = null;
}

function ok() {
  showDialog.value = false;
  if (msgCb.value) {
    msgCb.value();
  }
}

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});

defineExpose({
  open,
  close,
});
</script>

<style scoped>
.confirm-content {
  padding: 1rem 0;
}

.info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.info-text {
  margin: 0;
  text-align: center;
  color: var(--black);
  font-size: 1.2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.field-label {
  font-weight: 500;
  color: #424242;
  font-size: 14px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
